import _, { isArray } from "lodash";
import {
  DEFAULT_TEMPLATE_DETAILS,
  DocumentType,
} from "constants/TemplateEditor";
import { PageProperties } from "constants/TemplateEditor";
import { TemplateRule } from "interfaces";
import { TemplateStatus } from "components/EditorHome/Constants";

/**
 * @description The function `groupByGroupId` takes an array of data, groups it by `groupId`, and returns an array
 * of objects with `groupId`, `groupName`, and `categories` properties.
 * @param {Array<any>} data - An array of objects containing data to be grouped. Each object should have a "groupId"
 * property.
 * @returns an array of objects. Each object in the array represents a group and contains the following
 * properties:
 * - groupId: the ID of the group
 * - groupName: the name of the group
 * - categories: an array of objects representing the categories within the group. The categories are
 * grouped by categoryId.
 */
export const groupByGroupId = (data: Array<any>): Array<any> => {
  try {
    if (isArray(data)) {
      return _.chain(data)
        .groupBy("groupId")
        .toPairs()
        .map((value, key) => {
          const categories = value[1];
          return {
            groupId: value[0],
            groupName: categories[0].groupName,
            categories: groupByCategoryId(categories),
          };
        })
        .value();
    } else {
      return [];
    }
  } catch (error) {
    return [];
  }
};

/**
 * @description The function `groupByCategoryId` takes an array of data and groups it by the "categoryId" property,
 * returning an array of objects with the categoryId, categoryName, and templates.
 * @param data - An array of objects containing data to be grouped by category ID. Each object in the
 * array should have a "categoryId" property and a "categoryName" property.
 * @returns The function `groupByCategoryId` returns an array of objects. Each object in the array
 * represents a category and contains the following properties:
 * - `categoryId`: The ID of the category.
 * - `categoryName`: The name of the category.
 * - `templates`: An array of templates belonging to the category.
 */
const groupByCategoryId = (data: Array<any>): Array<any> => {
  try {
    if (isArray(data)) {
      return _.chain(data)
        .groupBy("categoryId")
        .toPairs()
        .map((value, key) => {
          const templates = value[1];
          return {
            categoryId: value[0],
            categoryName: templates[0].categoryName,
            displayName: templates[0]?.categoryDisplayName,
            templates,
          };
        })
        .value();
    } else {
      return [];
    }
  } catch (error) {
    return [];
  }
};

/**
 * @description The function `getTemplatesByGroupAndCategory` takes in an array of groups, a groupId, and a
 * categoryId, and returns an array of templates that belong to the specified group and category.
 * @param {Array<any>} groups - An array of objects representing different groups. Each group object has the
 * following properties:
 * @param {string} groupName - The groupId parameter is the identifier of the group you want to filter the
 * templates by.
 * @param {any} categoryId - The categoryId parameter is used to specify the ID of the category for
 * which you want to retrieve templates.
 * @returns The function `getTemplatesByGroupAndCategory` returns an array of templates.
 */
export const getTemplatesByGroupAndCategory = (
  groups: Array<any>,
  groupName: any,
  categoryId: any
): Array<any> => {
  try {
    const groupIndex = groups.findIndex(
      (group) => group.groupName == groupName
    );
    if (groupIndex > -1) {
      const categoryIndex = groups[groupIndex].categories.findIndex(
        (category: { categoryId: any }) => category.categoryId == categoryId
      );
      if (categoryIndex > -1) {
        const isEHF =
          groups[groupIndex].categories[categoryIndex].categoryName == "EHF";
        const templates = groups[groupIndex].categories[
          categoryIndex
        ].templates.filter((template: any) => template.templateId != 0);
        if (isEHF) {
          return [...templates];
        } else {
          return [{ ...DEFAULT_TEMPLATE_DETAILS }, ...templates];
        }
      } else {
        return [];
      }
    } else {
      return [];
    }
  } catch (error) {
    return [];
  }
};

/**
 * @description - Filter template data by URL parameters
 * @param {Array<any>} templates API response data
 * @param {object} filterProps properties for filtering
 * @returns {object} - filtered data
 */
export const getFilteredTemplateData = (
  templates: Array<any>,
  filterProps?: {
    searchText?: string | null;
    activityNo?: string | null;
    documentType?: string | null;
    templateStatus?: string | null;
  }
): Array<any> => {
  try {
    const { searchText, activityNo, documentType, templateStatus } =
      filterProps ?? {};

    let filteredTemplates = templates;

    if (documentType !== DocumentType.ALL) {
      filteredTemplates = filteredTemplates.filter(
        (template) =>
          template.outPutFormat.toLowerCase() == documentType?.toLowerCase()
      );
    }

    if (templateStatus != TemplateStatus.ALL) {
      const isActive = templateStatus == TemplateStatus.ACTIVE;
      filteredTemplates = filteredTemplates.filter(
        (template) => template.isActive == isActive
      );
    }

    const searchActivityNo = (activityNumbers: Array<string>) => {
      if (activityNo) {
        return activityNumbers?.some((activityNumber: any) =>
          String(activityNumber)?.includes(activityNo ? String(activityNo) : "")
        );
      } else {
        return true;
      }
    };

    if (Array.isArray(filteredTemplates)) {
      return filteredTemplates.filter((templateData: any) => {
        return Object.keys(templateData).some(
          () =>
            String(templateData["templateName"])
              ?.toLowerCase()
              ?.includes(searchText ? String(searchText)?.toLowerCase() : "") &&
            searchActivityNo(templateData["activityNumbers"])
        );
      });
    }
    return templates;
  } catch (e) {
    return templates;
  }
};

/**
 * @description - Get Page Properties by type
 * @param {DocumentType} documentType Page type
 * @returns {object} - properties data
 */
export const getPageProperties = (
  documentType: DocumentType | null | string
): typeof PageProperties.PDF => {
  try {
    switch (documentType) {
      case DocumentType.PDF:
        return PageProperties.PDF;
      case DocumentType.SMS:
        return PageProperties.SMS;
      case DocumentType.EHF:
        return PageProperties.EHF;
      case DocumentType.EMAIL:
        return PageProperties.EMAIL;
      default:
        return PageProperties.PDF;
    }
  } catch (e) {
    return PageProperties.PDF;
  }
};

/**
 * @description filter rule list by non empty template names
 * @param {Array<TemplateRule>} globalRules global rule list
 * @returns {Array<TemplateRule>} filtered rule list
 */
export const getFilteredRuleList = (
  globalRules: Array<TemplateRule>
): Array<TemplateRule> => {
  try {
    if (globalRules.length > 0) {
      return globalRules.filter((item: any) => item.templateName != null);
    } else return [];
  } catch (error) {
    return [];
  }
};

/**
 * @description Get menu content by selected id.
 * @param {any[]} groups - Group list
 * @param {string | null} groupId - Selected template group id
 * @returns {string} - Selected template group name
 */
export const getTemplateGroupName = (
  groups: any[],
  groupId: string | null | undefined
): string => {
  try {
    if (isArray(groups)) {
      const group = groups.find((group) => group.groupId == groupId);
      return group?.groupName;
    } else {
      return "";
    }
  } catch (error) {
    return "";
  }
};

/**
 * @description Get selected template category name
 * @param {any[]} groups - Group list
 * @param {string | null | undefined} groupId - Selected template group id
 * @param {string | null | undefined} categoryId - Selected template category id
 * @returns {string} - Selected template group name
 */
export const getTemplateCategoryName = (
  groups: any[],
  groupId: string | null | undefined,
  categoryId: string | null | undefined
): string => {
  try {
    if (isArray(groups)) {
      const group = _.find(groups, { groupId: groupId });
      const category = _.find(group?.categories, { categoryId: categoryId });
      return category?.categoryName;
    } else {
      return "";
    }
  } catch (error) {
    return "";
  }
};

/**
 * @description - Generate options list for document type selection
 * @param {any[]} documentTypes - Document types data
 * @returns {any[]} - Document type options
 */
export const getDocumentFormatOptions = (documentTypes: any[]): any[] => {
  try {
    if (isArray(documentTypes) && documentTypes.length > 0) {
      return documentTypes.map((documentType) => {
        return {
          label: documentType.channelDisplayName,
          value: documentType.channelCode,
        };
      });
    } else {
      return [];
    }
  } catch (error) {
    return [];
  }
};
